import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Download, FileText, LayoutGrid, LogOut, Moon, Plus, Settings, Sun } from "lucide-react";
import { useAuth } from "@/lib/auth";

const nav = [
  { to: "/dashboard", label: "Overview", icon: LayoutGrid, id: "nav-dashboard" },
  { to: "/documents", label: "Documents", icon: FileText, id: "nav-documents" },
  { to: "/settings", label: "Settings", icon: Settings, id: "nav-settings" },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const [installPrompt, setInstallPrompt] = useState(null);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    const h = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);

  const install = async () => {
    installPrompt?.prompt();
    setInstallPrompt(null);
  };

  return (
    <div className="min-h-screen grain">
      <header className="sticky top-0 z-40 border-b border-border bg-background/70 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 h-16 flex items-center gap-6">
          <NavLink to="/dashboard" className="font-display font-black text-xl tracking-tight" data-testid="brand-link">
            DocFlow
          </NavLink>
          <nav className="hidden md:flex items-center gap-1 ml-6">
            {nav.map(({ to, label, icon: Icon, id }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={id}
                className={({ isActive }) =>
                  `flex items-center gap-2 h-9 px-4 text-sm font-medium transition-colors ${isActive ? "bg-foreground text-background" : "hover:bg-muted"}`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {installPrompt && (
              <button onClick={install} data-testid="install-app-button" className="btn-press hidden sm:flex items-center gap-2 h-9 px-3 text-sm border border-border hover:border-foreground">
                <Download className="w-4 h-4" strokeWidth={1.5} /> Install
              </button>
            )}
            <button onClick={() => setDark(!dark)} data-testid="theme-toggle" className="h-9 w-9 grid place-items-center border border-border hover:border-foreground transition-colors">
              {dark ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
            </button>
            <button onClick={() => navigate("/documents/new")} data-testid="new-document-button" className="btn-press flex items-center gap-2 h-9 px-4 bg-primary text-primary-foreground text-sm font-semibold hover:bg-[#C04E2D]">
              <Plus className="w-4 h-4" strokeWidth={2} /> <span className="hidden sm:inline">New</span>
            </button>
            <div className="hidden sm:flex items-center gap-3 pl-3 border-l border-border">
              {user?.picture ? <img src={user.picture} alt="" className="w-8 h-8 object-cover" /> : <div className="w-8 h-8 bg-foreground text-background grid place-items-center font-display font-black text-sm">{user?.name?.[0]}</div>}
              <div className="leading-tight">
                <div className="text-sm font-semibold" data-testid="user-name">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="user-role">{user?.role}</div>
              </div>
            </div>
            <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button" className="h-9 w-9 grid place-items-center border border-border hover:border-foreground transition-colors" title="Sign out">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>
      <main className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8 py-8 sm:py-12 pb-28 md:pb-12">
        <Outlet />
      </main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/80 backdrop-blur-xl grid grid-cols-3">
        {nav.map(({ to, label, icon: Icon, id }) => (
          <NavLink key={to} to={to} data-testid={`${id}-mobile`} className={({ isActive }) => `flex flex-col items-center gap-1 py-3 text-[11px] font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <Icon className="w-5 h-5" strokeWidth={1.5} /> {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
